import { Skeleton } from '@mui/material';
import { ListBase, useTranslate, WithListContext } from 'react-admin';
import { fNumber, fShortenNumber } from 'src/utils/format-number';
import { ColorType } from 'src/theme/core';
import { ItemCard } from './ItemCard';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;
type DashboardTotalLabel = { text: string; tooltip?: string } | null;

type DashboardCardProps<
  TData extends Record<string, unknown> = Record<string, unknown>,
  TFilter extends Record<string, unknown> = Record<string, unknown>,
> = {
  resource: string;
  title: string;
  tooltip: string;
  icon: string;
  unitKey: string;
  color: ColorType;
  getValues: (data: TData) => number[];
  getTotalLabel?: (
    data: TData,
    total: number,
    translate: TranslateFn
  ) => DashboardTotalLabel;
  onData?: (data: TData) => void;
  filter: TFilter;
  initialData?: TData;
};

export const DashboardCard = <
  TData extends Record<string, unknown> = Record<string, unknown>,
  TFilter extends Record<string, unknown> = Record<string, unknown>,
>({
  resource,
  title,
  tooltip,
  icon,
  unitKey,
  color,
  getValues,
  getTotalLabel,
  onData,
  filter,
  initialData,
}: DashboardCardProps<TData, TFilter>) => {
  const translate = useTranslate();

  return (
    <ListBase resource={resource} filter={filter}>
      <WithListContext
        render={({ data, isLoading, isFetching }) => {
          const firstRecord = data?.[0];
          const record = (firstRecord ?? initialData ?? {}) as unknown as TData;
          if (onData && firstRecord) onData(firstRecord as unknown as TData);

          const total = getValues(record)?.reduce((a, b) => a + b, 0) ?? 0;
          const totalLabel = getTotalLabel?.(record, total, translate) ?? null;

          return isLoading ? (
            <Skeleton height="112px" width="100%" />
          ) : (
            <ItemCard
              title={translate(title)}
              total={totalLabel?.text ?? `${translate(unitKey)} ${fShortenNumber(total ?? 0)}`}
              totalTooltip={
                totalLabel?.tooltip ??
                `${translate(unitKey)} ${fNumber(total ?? 0, { maximumFractionDigits: 2 })}`
              }
              color={color}
              icon={icon}
              tooltip={translate(tooltip)}
              isValueLoading={isFetching}
            />
          );
        }}
      />
    </ListBase>
  );
};
